import React from 'react';
import { Link } from 'react-router-dom';

// Apple-inspired color palette (matching LandingPage.jsx)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#6e6e6e',
  gray700: '#636363',
  gray900: '#333333',
};

// Global font styles
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#000000',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
    color: '#333333',
  }
};

const MobileFooter = ({ activePage = "" }) => {
  const linkStyle = (isActive) => ({
    color: isActive ? colors.accent : colors.gray600,
    fontWeight: 500,
    letterSpacing: '-0.01em',
    transition: 'color 0.2s ease'
  });

  return (
    <footer className="bg-white border-t mt-auto relative overflow-hidden" 
      style={{
        borderColor: colors.gray100,
        fontFamily: fontStyles.fontFamily,
        paddingTop: '60px',
        paddingBottom: '100px',
        minHeight: '400px',
        background: 'linear-gradient(to bottom, white, #f8faff)'
      }}>
      {/* Simple Vertical Lines for Mobile Content Distribution */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto h-full relative" style={{ paddingLeft: 16, paddingRight: 16 }}>
          {/* Left vertical lines - mobile - matching static pages brightness */}
          <div className="absolute left-2 top-0 bottom-0 w-px" 
            style={{
              background: 'rgba(30, 64, 175, 0.18)',
              opacity: 0.8
            }} 
          />
          <div className="absolute left-5 top-0 bottom-0 w-px" 
            style={{
              background: 'rgba(30, 64, 175, 0.14)',
              opacity: 0.9
            }} 
          />
          {/* Right vertical lines - mobile - matching static pages brightness */}
          <div className="absolute right-2 top-0 bottom-0 w-px" 
            style={{
              background: 'rgba(30, 64, 175, 0.18)',
              opacity: 0.8
            }} 
          />
          <div className="absolute right-5 top-0 bottom-0 w-px" 
            style={{
              background: 'rgba(30, 64, 175, 0.14)',
              opacity: 0.9
            }} 
          />
        </div>
      </div>
      <div className="container mx-auto px-4">
        {/* Main footer content */}
        <div className="flex flex-col justify-between mb-8 relative z-10">
          {/* Company info */}
          <div className="mb-8">
            <div className="flex flex-col">
              <img src="/6.png" alt="SignPact Logo" width="140" height="44" className="h-12 mb-6" />
              <p className="text-base mb-6" style={{ color: colors.gray600, lineHeight: 1.6, maxWidth: "100%" }}>
                SignPact helps you create, manage, and sign contracts with AI-powered tools that simplify your workflow.
              </p>
              {/* Social Media Links */}
              <div className="flex space-x-5 mb-8">
                <a href="https://www.linkedin.com/company/signpact/" target="_blank" rel="noopener noreferrer" 
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="LinkedIn">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
                  </svg>
                </a>
                <a href="https://github.com/signpact" target="_blank" rel="noopener noreferrer" 
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="GitHub">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-10">
            {/* Product */}
            <div>
              <h3 className="text-base font-medium mb-5" style={{ color: colors.gray900 }}>Product</h3>
              <ul className="space-y-3.5">
                <li>
                  <a href="/#document-generation" className="text-sm hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "features")}>
                    Features
                  </a>
                </li>
                <li>
                  <Link to="/pricing" className="text-sm hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "pricing")}>
                    Pricing
                  </Link>
                </li>
                {/* <li>
                  <Link to="/plan-comparison" className="text-sm hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "plan-comparison")}>
                    Feature Comparison
                  </Link>
                </li> */}
                <li>
                  <Link to="/vision" className="text-sm hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "vision")}>
                    Vision
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-base font-medium mb-5" style={{ color: colors.gray900 }}>Legal</h3>
              <ul className="space-y-3.5">
                <li>
                  <Link to="/privacy" className="text-sm hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "privacy")}>
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "terms")}>
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/compliance" 
                    className="text-sm hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "compliance")}
                  >
                    Compliance
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer bottom */}
        <div className="pt-4 pb-6 border-t border-gray-200 flex flex-col justify-between items-center relative z-10">
          <p className="text-sm mb-0" style={{ color: colors.gray500 }}>
            © 2025 SignPact, Inc. All rights reserved.
          </p>
        </div>
      </div>
      
      {/* Background logo - simple mobile version */}
      <div className="absolute bottom-0 left-0 w-full" style={{
        zIndex: 0,
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        <div 
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '-60px',
            transform: 'translateX(-50%)',
            width: '110%',
            maxWidth: '570px',
            minWidth: '340px'
          }}
        >
          <img 
            src="/6.png" 
            alt="" 
            style={{
              width: '100%',
              height: 'auto',
              opacity: 0.12,
              filter: 'grayscale(100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.1) 85%, rgba(0,0,0,0) 100%)',
              maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.1) 85%, rgba(0,0,0,0) 100%)',
              transition: 'all 0.5s ease'
            }}
          />
          
        </div>
      </div>
      
    </footer>
  );
};

export default MobileFooter; 